import { useForm } from "react-hook-form";
import React, { useEffect, useMemo, useState } from "react";
import { Box, Container, Grid, Typography, MenuItem, Select, FormControl, Chip } from "@mui/material";
import Button from "../../common/Button";
import {
  BusinessOutlined,
  Cancel,
  CheckCircle,
  DescriptionOutlined,
  DriveFileRenameOutline,
  DrawOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import CustomTextField from "../../common/CustomTextField";
import SignaturePad, {
  SignatureData,
} from "../../common/signature/SignaturePad";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import PDFViewer from "../../common/pdf/PDFViewer";
import { SingleAlertInfo, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import OptionsPopup, { OptionPopupObjectProps } from "../../common/OptionsPopup";
import authSlice, { logout, relogin, updateElementInAuthSlice, useAuthSlice } from "../../../redux/slice/authSlice";
import { fullName } from "../../../utils/Utility";
import { baaDetailSign, previewAgreement, resetBusinessAgreementsList, useCreateBusinessAgreementSlice } from "../../../redux/slice/baa/createBusinessAgreementSlice";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { setDataToLocalStorage } from "../../../utils/StorageUtils";
import { CAN_ACCESS_APPLICATION } from "../../../constants/applicationConstants";
import { useNavigation } from "../../../hooks/useNavigation";
import { useBranding } from "../../../BrandingContext";
import { fetchPracticeById } from "../../../redux/slice/practiceSlice";
import { fetchAgencyById } from "../../../redux/slice/agency/AgencySlice";
import { fetchManufacturerDataById } from "../../../redux/slice/manufacturer/ManufacturerSlice";
import CustomAddressTextField from "../../common/CustomAddressTextField";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { CodeTable, getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { useModalStyles } from "../../common/useModalStyles";
import { SectionHeader } from "../../ui/ContentSection";

type BaaAgreement = {
  signature: string,
  name: string,
  legalNameOfPractice: string,
  subcontractorLegalName: string,
  stateOfFormation: string,
  entityType: string,
  title: string,
  contactName: string,
  contactTitle: string,
  contactAddress: string,
  contactEmail: string,
  entityId:number,
  address: any;
};

type PracticeData = {
  practiceId: number;
  practiceName: string;
  legalName: string;
  stateOfFormation: string;
  entityType: string,
  contactName: string;
  contactTitle: string;
  contactAddress: string;
  contactEmail: string;
  isPreviewed: boolean;
};

const initialBaaAgreement: BaaAgreement = {
  signature: "",
  name: "",
  legalNameOfPractice: "",
  subcontractorLegalName: "",
  stateOfFormation: "",
  entityType: "",
  title: "",
  contactName: "",
  contactTitle: "",
  contactAddress: "",
  contactEmail: "",
  entityId: 0,
  address: ""
};

const facts = [
  { title: "As part of our commitment to compliance with the Health Insurance Portability and Accountability Act (HIPAA), all users who access this system must sign our revised Business Associate Agreement (BAA). By signing this agreement, you acknowledge and agree to the terms and conditions for the proper use, security, and confidentiality of PHI." },
  { title: "Please review the BAA carefully by clicking the preview button. If you agree to the terms, proceed by signing the document." },
];

interface BaaAgreementProps {}

const BaaAgreement: React.FC<BaaAgreementProps> = () => {

  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const { control, handleSubmit, getValues, setValue, formState: { errors } } =
    useForm<BaaAgreement>({
      defaultValues: initialBaaAgreement,
    });

  const { userInfo } = useAuthSlice();
  const location = useLocation();
  const {
    tokens: mTokens,
    isDark,
    primaryBtnSx,
    cancelBtnSx,
    inputFieldSx,
  } = useModalStyles();

  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden" as const,
    boxShadow: isDark ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const sectionBodySx = { px: { xs: 2, md: 2.8 }, py: 2 };

  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.7, display: "flex", alignItems: "center", gap: 0.4 }}>
      {label}{required && <Box component="span" sx={{ color: mTokens.accent }}>*</Box>}
    </Typography>
  );

  const controlHeight = "42px";
  const controlHeightSx = {
    minHeight: `${controlHeight} !important`,
    height: `${controlHeight} !important`,
    maxHeight: `${controlHeight} !important`,
    boxSizing: "border-box !important",
    alignItems: "center",
  };
  const mergeControlSx = (base: object) => {
    const nested = (base as Record<string, unknown>)["& .MuiOutlinedInput-root, & .MuiInputBase-root"];
    return {
      ...(nested && typeof nested === "object" ? nested : {}),
      ...controlHeightSx,
    };
  };
  const dropdownFieldWrapSx = (hasError = false) => {
    const base = inputFieldSx(hasError);
    return {
      ...base,
      width: "100%",
      "& .MuiAutocomplete-root, & .MuiFormControl-root, & .MuiTextField-root": { width: "100%", maxWidth: "100%" },
      "& .MuiOutlinedInput-root, & .MuiInputBase-root": mergeControlSx(base),
      "& .MuiSelect-select": {
        display: "flex !important",
        alignItems: "center",
        py: "0 !important",
        height: "24px !important",
        minHeight: "24px !important",
        lineHeight: "24px !important",
        overflow: "hidden",
        boxSizing: "border-box",
      },
      "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
        padding: "0 38px 0 12px !important",
        flexWrap: "nowrap !important",
        overflow: "hidden !important",
      },
      "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input, & .MuiInputBase-input": {
        paddingTop: "8px !important",
        paddingBottom: "8px !important",
        height: "24px !important",
        lineHeight: "24px !important",
        boxSizing: "border-box",
      },
      "& .MuiAutocomplete-endAdornment": {
        top: "50% !important",
        transform: "translateY(-50%)",
        right: "8px !important",
      },
    };
  };
  const textFieldWrapSx = (hasError = false) => dropdownFieldWrapSx(hasError);

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
    "&.Mui-disabled": {
      opacity: 0.45,
      boxShadow: "none",
      color: "#fff",
      "& .MuiTypography-root": { color: "#fff" },
    },
  };

  const previewBtnSx = {
    textTransform: "none" as const,
    fontWeight: 700,
    fontSize: "0.9rem",
    borderRadius: "10px",
    py: 1.2,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap" as const,
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
      "& .MuiTypography-root": { color: "#fff" },
      "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
    },
  };

  const cancelActionBtnSx = {
    ...cancelBtnSx,
    color: mTokens.error,
    "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
    "&:hover": {
      ...cancelBtnSx["&:hover"],
      color: mTokens.error,
      "& .MuiTypography-root": { color: "inherit" },
    },
  };
  const baaDetailId = location.state?.baaDetailId;
  const searchBaaDetails = location.state?.searchBaaDetails;
  const [isPreviewClicked, setIsPreviewClicked] =  React.useState(0);
  const [showPDF, setShowPDF] = React.useState(false);
  const [practicesData, setPracticesData] = React.useState<PracticeData[]>([]);
  const [selectedPracticeId, setSelectedPracticeId] = React.useState<number | null>(null);
  const { baaDocument } = useCreateBusinessAgreementSlice();
  const [showOptionPopup, setShowOptionsPopup] = React.useState<OptionPopupObjectProps>({ message: "", show: false });
  const [errorIdentifiers, setErrorIdentifiers] = React.useState<string[]>([]);
  const [alertShown, setAlertShown] = useState(false);
  const [isBAASkipped, setIsBAASkipped] = useState(false);
  const [isLoading, setIsLoading] = useState(true); 
  const [showAddressField, setShowAddressField] = useState(false);
  const { allCodeTablesValuesMap ,allCodeTableValueFetchStatus} = useCodetablesSlice();
  const [entityTypes, setEntityTypes] = React.useState<CodeTable[]>([]);
  const [stateOfFormations, setStateOfFormations] = React.useState<CodeTable[]>([]);

  const branding = useBranding();

  // Check if all practices have been previewed
  const allPracticesPreviewed = practicesData.length > 0 && practicesData.every(p => p.isPreviewed);
  const previewedCount = practicesData.filter(p => p.isPreviewed).length;

  useEffect(()=>{
    if (userInfo && userInfo.canAccessApplicationValue) {
      const items = userInfo.canAccessApplicationValue.split(";");
      items.forEach(applicationName => {
        if(CAN_ACCESS_APPLICATION.BAA_MANAGEMENT === applicationName && baaDetailId) {
          setIsBAASkipped(true);
        }
      });
    } 
    setIsLoading(false);
    
  },[userInfo.canAccessApplicationValue]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedStatesList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "USStateList"),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setStateOfFormations(sortedStatesList);
      setEntityTypes(getCodeTableValuesByTableName(allCodeTablesValuesMap, "BAAEntityTypes"));
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  const baaEntityTypes = useMemo(() => {
    return [
      ...entityTypes.map(item => ({
        label: item.displayValue,
        value: item.keyName
      }))
    ];
  }, [entityTypes]);

  const baaStateOfFormation = useMemo(() => {
    return [
      ...stateOfFormations.map(item => ({
        label: item.displayValue,
        value: item.keyName
      }))
    ];
  }, [stateOfFormations]);

  useEffect(() => {
    const fetchAllPracticesDetails = async () => {
      if (userInfo?.entityIds && userInfo.entityIds.length > 0 && userInfo.entityName === "PRACTICE") {
        const practicesArray: PracticeData[] = [];
        
        for (const practiceId of userInfo.entityIds) {
          try {
            const response = await dispatch(fetchPracticeById(practiceId.toString()));
            const practiceData = response.payload?.data;
            
            if (practiceData?.isBaaSigned === false) {
            const practiceName = practiceData?.legalName || practiceData?.practiceName;
            const firstName = userInfo.firstName ?? '';
            const middleName = userInfo?.middleName ?? '';
            const lastName = userInfo?.lastName ?? '';

            const contactName = [firstName, middleName, lastName].filter(Boolean).join(' ');
            const contactEmail = userInfo.userName ?? '';

            const location = practiceData?.practiceLocations?.[0];
            const contactAddress = [
              location?.streetAddress,
              location?.unitNumber,
              `${location?.city}, ${location?.state} ${location?.zipCode}`
            ].filter(Boolean).join(', ');

            practicesArray.push({
              practiceId: practiceData?.practiceId,
              practiceName: practiceName,
              legalName: practiceName,
              stateOfFormation: "",
              entityType: "",
              contactName: contactName,
              contactTitle: "",
              contactAddress: contactAddress,
              contactEmail: contactEmail,
              isPreviewed: false
            });
          }

          } catch (error) {
            console.error("Error fetching practice details:", error);
          }
        }
        
        // Set first practice as selected by default
        if (practicesArray.length > 0) {
          setPracticesData(practicesArray);

          setSelectedPracticeId(practicesArray[0].practiceId);
          loadPracticeDataToForm(practicesArray[0]);
        }

        if (userInfo.userRole === "ROLE_PRACTICE_TX") {
          setShowAddressField(true);
        }
        
      } else if (userInfo?.entityIds && userInfo.entityIds.length > 0 && userInfo.entityName === "AGENCY") {
        const firstAgencyId = userInfo.entityIds[0];
        try {
          const response = await dispatch(fetchAgencyById(firstAgencyId));
          const agency = response.payload?.data;
          const agencyName = agency?.legalName || agency?.agencyName;
          // const agencyAddress = [
          //   agency?.streetAddress,
          //   agency?.unitNumber,
          //   `${agency?.city}, ${agency?.state} ${agency?.zipcode}`].filter(Boolean).join(', ');

          setValue("subcontractorLegalName", agencyName);
          setValue("contactName", fullName(null, userInfo.firstName, userInfo.middleName, userInfo.lastName));
          setValue("contactTitle", "");
          // setValue("contactAddress", agencyAddress);
          setValue("contactAddress", "");
          setValue("contactEmail", userInfo.userName);
          setValue("entityId", agency?.agencyId);
          setShowAddressField(true);

        } catch (error) {
          console.error("Error fetching agency details:", error);
        }
      } else if (userInfo?.entityIds && userInfo.entityIds.length > 0 && userInfo.entityName === "MANUFACTURER") {
        const firstManufacturerId = userInfo.entityIds[0];
        try {
          const response = await dispatch(fetchManufacturerDataById(firstManufacturerId));
          const manufacturer = response.payload?.data?.data;
          const manufacturerName = manufacturer.legalName || manufacturer?.manufacturerName;

          if(userInfo.userRole !== "ROLE_MANUFACTURERIVR"){
            setValue("subcontractorLegalName", manufacturerName);
          }
          setValue("contactName", fullName(null, userInfo.firstName, userInfo.middleName, userInfo.lastName));
          setValue("contactTitle", "");
          setValue("contactAddress", "");
          setValue("contactEmail", userInfo.userName);
          setValue("entityId", manufacturer?.manufacturerId);
          setShowAddressField(true);

        } catch (error) {
          console.error("Error fetching manufacturer details:", error);
        }
      } else if (userInfo?.entityIds && userInfo.entityIds.length > 0 && userInfo.entityName === "ACCOUNTEXECUTIVE") {
        const firstAgencyId = userInfo.entityIds[0];
        try {
          const response = await dispatch(fetchAgencyById(firstAgencyId));
          const agency = response.payload?.data;

          setValue("contactName", fullName(null, userInfo.firstName, userInfo.middleName, userInfo.lastName));
          setValue("contactTitle", "");
          setValue("contactAddress", "");
          setValue("contactEmail", userInfo.userName);
          setValue("entityId", agency?.agencyId);
          setShowAddressField(true);

        } catch (error) {
          console.error("Error fetching agency details:", error);
        }
      } else if (userInfo?.entityIds && userInfo.entityIds.length > 0 && userInfo.entityName === "BILLER") {
        const firstBillerId = userInfo.entityIds[0];
        try {
          setValue("contactName", fullName(null, userInfo.firstName, userInfo.middleName, userInfo.lastName));
          setValue("contactTitle", "");
          setValue("contactAddress", "");
          setValue("contactEmail", userInfo.userName);
          setValue("entityId", firstBillerId);
          setShowAddressField(true);

        } catch (error) {
          console.error("Error fetching agency details:", error);
        }
      } else if (userInfo?.entityIds && userInfo.entityIds.length > 0 && userInfo.entityName === "ADMIN") {
        try {
          setValue("contactName", fullName(null, userInfo.firstName, userInfo.middleName, userInfo.lastName));
          setValue("contactTitle", "");
          setValue("contactAddress", "");
          setValue("contactEmail", userInfo.userName);
          setShowAddressField(true);

        } catch (error) {
          console.error("Error fetching agency details:", error);
        }
      }
    };

    fetchAllPracticesDetails();
  }, [userInfo?.entityIds, userInfo?.entityName, userInfo.userRole, setValue]);

  const loadPracticeDataToForm = (practice: PracticeData) => {
    if (userInfo.userRole !== "ROLE_PRACTICE_TX") {
      setValue("legalNameOfPractice", practice.legalName);
    }
    setValue("stateOfFormation", practice.stateOfFormation);
    setValue("entityType", practice.entityType);
    setValue("contactName", practice.contactName);
    setValue("contactTitle", practice.contactTitle);
    setValue("contactAddress", practice.contactAddress);
    setValue("contactEmail", practice.contactEmail);
    setValue("entityId", practice.practiceId);
  };

  const savePracticeDataFromForm = (practiceId: number) => {
    const legalName = getValues("legalNameOfPractice");
    const stateOfFormation = getValues("stateOfFormation");
    const entityType = getValues("entityType");
    const contactAddress = getValues("contactAddress");
    
    setPracticesData(prevData =>
      prevData.map(p =>
        p.practiceId === practiceId
          ? {
            ...p,
            legalName,
            stateOfFormation,
            entityType,
            // Update contact address for ROLE_PRACTICE_TX users
            ...(userInfo.userRole === "ROLE_PRACTICE_TX" && { contactAddress })
          }
          : p
      )
    );
  };

  const handlePracticeChange = (practiceId: number) => {
    // Save current practice data before switching
    if (selectedPracticeId) {
      savePracticeDataFromForm(selectedPracticeId);

      // For ROLE_PRACTICE_TX users, also save the address if modified
      if (userInfo.userRole === "ROLE_PRACTICE_TX") {
        const currentContactAddress = getValues("contactAddress");
        setPracticesData(prevData =>
          prevData.map(p =>
            p.practiceId === selectedPracticeId
              ? { ...p, contactAddress: currentContactAddress }
              : p
          )
        );
      }
    }
    
    const practice = practicesData.find(p => p.practiceId === practiceId);
    if (practice) {
      setSelectedPracticeId(practiceId);
      loadPracticeDataToForm(practice);
    }
  };

  const skipBAASigning = () => {
    dispatch(updateElementInAuthSlice({
      elementName: "userInfo",
      value: { 
        ...userInfo, 
        baaSigningSkipped: true 
      }
    }));

    navigate("/");
  }

  const handleSignatureChange = (signatureData: SignatureData) => {
    setValue("signature", signatureData.signature);
    setValue("name", signatureData.typedText);

    setErrorIdentifiers((prevValues) => {
      prevValues = prevValues.filter((value) => value !== "signature");
      return prevValues;
    });
  };

  const cancelOptionPopup = () => {
    setShowOptionsPopup({ message: "", show: false });
  };

  const cancelBAASigning = () => {
    if(baaDetailId && searchBaaDetails){
      navigate("/baa_Detail", {state: { baaListSearchCriteria : searchBaaDetails }});
    } else {
      dispatch(logout());
      navigate("/");
    }
  };

  const onPreviewClick = async () => {
    if (!selectedPracticeId && userInfo.entityName === "PRACTICE") {
      let errorObj: SingleAlertInfo = {
        message: `Please select a practice to preview`,
        alertType: "error",
      };
      dispatch(setSingleAlertObj(errorObj));
      return;
    }

    // Save current practice data before preview
    if (selectedPracticeId) {
      savePracticeDataFromForm(selectedPracticeId);
    }

    const signature = getValues("signature");
    const name = getValues("name");
    const legalNameOfPractice = getValues("legalNameOfPractice");
    const subcontractorLegalName = getValues("subcontractorLegalName");
    const stateOfFormation = getValues("stateOfFormation");
    const entityType = getValues("entityType");
    const title = getValues("title")
    const contactName = getValues("contactName")
    const contactTitle = getValues("contactTitle")
    const contactAddress = getValues("contactAddress")
    const contactEmail = getValues("contactEmail")
    
    const data = {
      signature: signature,
      baaDetailId: baaDetailId,
      name: name,
      title: title,
      legalNameOfPractice: legalNameOfPractice,
      subcontractorLegalName: subcontractorLegalName,
      stateOfFormation: stateOfFormation,
      entityType: entityType,
      contactName: contactName,
      contactTitle: contactTitle,
      contactAddress: contactAddress,
      contactEmail: contactEmail,
      userId: userInfo.userId,
      userRole: userInfo.userRole,
    }

    const actionResult = await dispatch(previewAgreement(data));
    const documentData = actionResult.payload;
    
    if (documentData && documentData.content) {
      setShowPDF(true);
      
      // Mark the current practice as previewed
      setPracticesData(prevData => 
        prevData.map(p => 
          p.practiceId === selectedPracticeId 
            ? { ...p, isPreviewed: true } 
            : p
        )
      );
    }
    setIsPreviewClicked(isPreviewClicked + 1);
    
    cancelOptionPopup();
  };

  const loadStartingPageAfterSign = () => {
    let successObj: SingleAlertInfo = {
      message: `Business Associate Agreement has been signed for all practices`,
      alertType: "success",
    };
    dispatch(setSingleAlertObj(successObj));

    dispatch(updateElementInAuthSlice({
      elementName: "userInfo",
      value: { 
        ...userInfo, 
        userSigned: true 
      }
    }));
  };

  const handleAddressChange = () => {
    const address = getValues("address");

    if (!address) return;

    const streetAddress = address.StreetAddress;
    const unitNumber = address.UnitNumber;
    const city = address.City;
    const state = address.State;
    const zipCode = address.Zipcode;

    const formattedAddress = [
      streetAddress,
      unitNumber,
      `${city}, ${state} ${zipCode}`
    ].filter(Boolean).join(', ');

    setValue("contactAddress", formattedAddress);
  };

  const signBusinessAgreement = async (sideAgreementObj: any) => {
    // Save current practice data before signing
    if (selectedPracticeId) {
      savePracticeDataFromForm(selectedPracticeId);
    }

    let data: any;

    // Check if multiple practices scenario
    if (practicesData.length > 1) {
      // Multiple practices - send practices array
      const practicesDetails = practicesData.map(practice => ({
        entityId: practice.practiceId,
        legalNameOfPractice: practice.legalName,
        stateOfFormation: practice.stateOfFormation,
        entityType: practice.entityType,
        contactName: practice.contactName,
        contactTitle: practice.contactTitle,
        contactAddress: practice.contactAddress,
        contactEmail: practice.contactEmail,
      }));

      data = {
        baaDetailId: baaDetailId ? baaDetailId : null,
        userId: userInfo.userId,
        userRole: userInfo.userRole,
        signature: sideAgreementObj.signature,
        name: sideAgreementObj.name,
        title: sideAgreementObj.title,
        subcontractorLegalName: sideAgreementObj.subcontractorLegalName,
        practices: practicesDetails,
      }
    } else {
      // Single entity (Practice/Agency/Manufacturer) - send flat structure
      data = {
        baaDetailId: baaDetailId ? baaDetailId : null,
        userId: userInfo.userId,
        userRole: userInfo.userRole,
        signature: sideAgreementObj.signature,
        name: sideAgreementObj.name,
        entityId: sideAgreementObj.entityId,
        title: sideAgreementObj.title,
        legalNameOfPractice: sideAgreementObj.legalNameOfPractice,
        subcontractorLegalName: sideAgreementObj.subcontractorLegalName,
        stateOfFormation: sideAgreementObj.stateOfFormation,
        entityType: sideAgreementObj.entityType,
        contactName: sideAgreementObj.contactName,
        contactTitle: sideAgreementObj.contactTitle,
        contactAddress: sideAgreementObj.contactAddress,
        contactEmail: sideAgreementObj.contactEmail,
      }
    }

    const response = await dispatch(baaDetailSign(data));
    
    if (response?.payload?.data === "SUCCESS") {
      loadStartingPageAfterSign();

      if(baaDetailId && searchBaaDetails){
        navigate("/baa_Detail", {state: { baaListSearchCriteria : searchBaaDetails }});
      } else {
        navigate("/");
      }
    } else {
      let errorObj: SingleAlertInfo = {
        message: `There is an error while signing the agreements`,
        alertType: "error",
      };
      dispatch(setSingleAlertObj(errorObj));
      dispatch(logout());
      navigate("/");
    }
    
    cancelOptionPopup();
  };

  const onSubmit = async (obj: any) => {

    let {
      documents,
      ...sideAgreementObj
    } = obj;

    if (userInfo.entityName === "PRACTICE" && !allPracticesPreviewed) {
      let previewAgreementAlertObj: SingleAlertInfo = {
        message: `Please preview the agreement for all practices before signing. (${previewedCount}/${practicesData.length} previewed)`,
        alertType: "error",
      };
      dispatch(setSingleAlertObj(previewAgreementAlertObj));
    } 
    else if (isPreviewClicked <= 0 ) {
      let previewAgreementAlertObj: SingleAlertInfo = {
        message: `Please "Preview" the agreement to continue`,
        alertType: "error",
      };
      dispatch(setSingleAlertObj(previewAgreementAlertObj));
    }
    else if (sideAgreementObj?.signature === "") {
      setErrorIdentifiers((prevValues) => {
        const newValues = [...prevValues];
        newValues.push("signature");
        return newValues;
      });
    } 
    else {
      let confirmMessage = "Do you really want to sign the agreement?";

      if (userInfo.entityName === "PRACTICE" && practicesData.length > 1) {
        confirmMessage = `Do you really want to sign the agreement for all ${practicesData.length} practices?`;
      }

      setShowOptionsPopup({
        message: confirmMessage,
        show: true,
        variant: "confirm",
        buttons: [
          {
            name: "Confirm",
            color: "primary",
            onClick: () => signBusinessAgreement(sideAgreementObj),
          },
          { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
        ],
      });
    }
  };

  let signatureFieldName = "signature";
  let name = "name";

  const signaturePadValidations = {
    title: { 
      required: "Title is required" 
    },
    name: {
      required: "Name is required.",
    },
    signature: {
      required: "Signature is required.",
    },
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 2, sm: 3 },
          // The app footer is position:fixed, so leave room for it or it overlaps
          // the action buttons at the bottom of the form.
          pb: { xs: 12, sm: 10 },
        }}
      >
        {showOptionPopup.show && (
        <OptionsPopup
          open={showOptionPopup.show}
          onClose={() => setShowOptionsPopup({ message: "", show: false })}
          variant={showOptionPopup.variant}
          message={<>{showOptionPopup.message}</>}
          buttons={showOptionPopup.buttons}
          title={showOptionPopup.title}
          obj={showOptionPopup.obj}
        />
      )}
      {showPDF && <PDFViewer
        isOpen={showPDF}
        onClose={() => setShowPDF(false)}
        title={"Business Associate Agreement"}
        base64String={baaDocument?.content || ""}
        fileName={"Business Associate Agreement" + userInfo.userId}
      />}

        <Container component="main" maxWidth="lg" disableGutters sx={{ width: "100%" }}>
          <Box sx={{ textAlign: "center", mb: 2.5 }}>
            <img
              alt="logo"
              src={isDark ? branding?.logoWhite : branding?.logo || require("../../../branding-assets/v3biomed/logo.png")}
              style={{
                height: "72px",
                objectFit: "contain",
                maxWidth: "100%",
              }}
            />
            <Typography
              component="h1"
              sx={{
                mt: 1.5,
                fontSize: { xs: "1.35rem", sm: "1.6rem" },
                fontWeight: 800,
                color: mTokens.textPrimary,
                lineHeight: 1.15,
              }}
            >
              Business Associate Agreement
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {practicesData.length > 1 && (
              <Box sx={cardSx}>
                <SectionHeader icon={<BusinessOutlined fontSize="small" />} title="Multiple Practices Detected" />
                <Box sx={sectionBodySx}>
                  <Typography sx={{ mb: 2, fontSize: "0.84rem", color: mTokens.textSecondary, lineHeight: 1.6 }}>
                    You have {practicesData.length} practices. Please preview the agreement for each practice individually before signing.
                  </Typography>
                  <FieldLabel label="Select Practice to Preview" required />
                  <Box sx={dropdownFieldWrapSx()}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={selectedPracticeId || ''}
                        onChange={(e) => handlePracticeChange(e.target.value as number)}
                        displayEmpty
                        renderValue={(value) => {
                          const practice = practicesData.find((p) => p.practiceId === value);
                          return practice?.practiceName || "";
                        }}
                      >
                        {practicesData.map((practice) => (
                          <MenuItem key={practice.practiceId} value={practice.practiceId}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                              <span>{practice.practiceName}</span>
                              {practice.isPreviewed && (
                                <Chip
                                  label="Previewed"
                                  size="small"
                                  icon={<CheckCircle />}
                                  sx={{
                                    ml: 2,
                                    fontWeight: 700,
                                    color: mTokens.accent,
                                    backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(13,148,136,0.12)",
                                    border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(13,148,136,0.25)"}`,
                                    "& .MuiChip-icon": { color: mTokens.accent },
                                  }}
                                />
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.84rem", color: mTokens.textSecondary }}>
                      Preview Progress: {previewedCount}/{practicesData.length}
                    </Typography>
                    {allPracticesPreviewed && (
                      <Chip
                        label="All Practices Previewed - Ready to Sign"
                        icon={<CheckCircle />}
                        sx={{
                          fontWeight: 700,
                          color: mTokens.accent,
                          backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(13,148,136,0.12)",
                          border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(13,148,136,0.25)"}`,
                          "& .MuiChip-icon": { color: mTokens.accent },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            <Box sx={cardSx}>
              <SectionHeader icon={<DescriptionOutlined fontSize="small" />} title="Agreement Details" />
              <Box sx={sectionBodySx}>
                <Grid container spacing={2.5}>
                  {userInfo.entityName === "PRACTICE" && userInfo.userRole === "ROLE_PRACTICEADMIN" ?
                    <Grid item md={4} xs={12}>
                      <FieldLabel label="Practice Name" required />
                      <Box sx={textFieldWrapSx(!!errors.legalNameOfPractice)}>
                        <CustomTextField
                          controllerName={`legalNameOfPractice`}
                          control={control}
                          label=""
                          size="small"
                          rules={{ required: "Practice Legal Name is required."}}
                        />
                      </Box>
                    </Grid>
                    :
                    <Grid item md={4} xs={12}>
                      <FieldLabel label="Legal Business Name" required />
                      <Box sx={textFieldWrapSx(!!errors.subcontractorLegalName)}>
                        <CustomTextField
                          controllerName={`subcontractorLegalName`}
                          control={control}
                          label=""
                          size="small"
                          rules={{ required: "Legal Business Name is required."}}
                        />
                      </Box>
                    </Grid>
                  }
                  <Grid item md={5} xs={12}>
                    <FieldLabel label="Entity Type" required />
                    <Box sx={dropdownFieldWrapSx(!!errors.entityType)}>
                      <CustomDropdownField
                        name={`entityType`}
                        control={control}
                        label=""
                        size="small"
                        options={baaEntityTypes}
                        rules={{ required: "Entity Type is required."}}
                      />
                    </Box>
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <FieldLabel label="State of Formation" required />
                    <Box sx={dropdownFieldWrapSx(!!errors.stateOfFormation)}>
                      <CustomDropdownField
                        name={`stateOfFormation`}
                        control={control}
                        label=""
                        size="small"
                        options={baaStateOfFormation}
                        rules={{ required: "State of Formation is required." }}
                      />
                    </Box>
                  </Grid>
                  {showAddressField &&
                    <Grid item md={12} xs={12}>
                      <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: mTokens.textPrimary, mb: 1 }}>
                        Address
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <FieldLabel label="Street Address" required />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FieldLabel label="Unit Number" />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FieldLabel label="City" required />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FieldLabel label="State" required />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FieldLabel label="Zip Code" required />
                        </Grid>
                      </Grid>
                      <Box sx={textFieldWrapSx()}>
                        <CustomAddressTextField
                          control={control}
                          name="address"
                          useAutoComplete={true}
                          setValue={setValue}
                          size="small"
                          isRequired={true}
                          usePlaceholder={true}
                          onChange={handleAddressChange}
                        />
                      </Box>
                    </Grid>
                  }
                </Grid>

                <Box mt={2.5} sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                  {facts.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.25,
                        p: 1.4,
                        borderRadius: "12px",
                        border: `1px solid ${mTokens.border}`,
                        backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.025)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          flexShrink: 0,
                          backgroundColor: `${mTokens.accent}1a`,
                          border: `1px solid ${mTokens.accent}33`,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircle sx={{ color: mTokens.accent, fontSize: "18px" }} />
                      </Box>
                      <Typography sx={{ fontSize: "0.86rem", color: mTokens.textSecondary, lineHeight: 1.65, pt: "4px" }}>{item.title}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={cardSx}>
                <SectionHeader icon={<DrawOutlined fontSize="small" />} title="Signature" />
                <Box sx={sectionBodySx}>
                  <SignaturePad
                    isError={errorIdentifiers?.includes("signature")}
                    onSignatureChange={handleSignatureChange}
                    name={signatureFieldName}
                    textName={name}
                    control={control}
                    validationRules={signaturePadValidations}
                    setSignOutside={
                      userInfo &&
                      fullName(
                        null,
                        userInfo.firstName,
                        userInfo.middleName,
                        userInfo.lastName
                      )
                    }
                    enableTitle={true}
                  />
                </Box>
              </Box>

              <Box
                display="flex"
                justifyContent="flex-end"
                flexDirection={{ xs: "column", sm: "row" }}
                gap={1.2}
                sx={{ mt: 2 }}
              >
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<VisibilityOutlined />}
                  onClick={onPreviewClick}
                  removeUpperCase
                  fullWidth
                  sx={{ ...previewBtnSx, minWidth: { sm: 140 }, width: { xs: "100%", sm: "auto" } }}
                >
                  Preview
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<DriveFileRenameOutline />}
                  data-allow-submit="true"
                  disabled={userInfo.entityName === "PRACTICE" ? !allPracticesPreviewed : false}
                  removeUpperCase
                  fullWidth
                  sx={{ ...primaryActionBtnSx, minWidth: { sm: 160 }, width: { xs: "100%", sm: "auto" } }}
                >
                  Sign{practicesData.length > 1 && ` All (${practicesData.length})`}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={cancelBAASigning}
                  removeUpperCase
                  fullWidth
                  sx={{ ...cancelActionBtnSx, minWidth: { sm: 140 }, width: { xs: "100%", sm: "auto" } }}
                >
                  Cancel
                </Button>
              </Box>
            </form>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default BaaAgreement;
